import React, { useRef, useState, useEffect } from "react";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import ScreenerGrid from "./ScreenerGrid";

gsap.registerPlugin(useGSAP);

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const OPEN_DURATION = 0.3;

const Caret = ({ open }) => {
  return (
    <svg
      className="caret"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        transform: `rotate(${open ? 180 : 0}deg)`,
        transition: prefersReducedMotion() ? "none" : "transform 0.25s ease",
        borderRadius: "999px",
        opacity: open ? 1 : 0.2,
      }}
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
};

const AccordionItem = ({
  title,
  children,
  isActive,
  delayClose,
  onToggle,
  buttonRef,
  panelId,
  buttonId,
  scrollParentRef,
}) => {
  const contentRef = useRef(null);
  const itemRef = useRef(null);

  const [shouldRenderChildren, setShouldRenderChildren] = useState(isActive);
  const closeTimeout = useRef(null);

  // Ensure content mounts immediately on open
  useEffect(() => {
    if (isActive) {
      clearTimeout(closeTimeout.current);
      setShouldRenderChildren(true);
    }
  }, [isActive]);

  // Open animation (GSAP)
  useGSAP(
    () => {
      const el = contentRef.current;
      const itemEl = itemRef.current;

      if (!el || !isActive) return;

      // Kill any in-flight animations (important when toggling quickly)
      gsap.killTweensOf(el);

      // Reduced motion: snap open + scroll immediately
      if (prefersReducedMotion()) {
        gsap.set(el, {
          height: "auto",
          opacity: 1,
        });

        if (scrollParentRef?.current && itemEl) {
          scrollParentRef.current.scrollTo({
            top: itemEl.offsetTop - 100,
            behavior: "auto",
          });
        }

        return;
      }

      /**
       * IMPORTANT:
       * Force a known closed start state even if the item
       * mounted as active (defaultActiveKey case).
       */
      gsap.set(el, {
        height: 0,
        opacity: 0,
      });

      const targetHeight = el.scrollHeight;

      gsap.to(el, {
        height: targetHeight,
        opacity: 1,
        duration: OPEN_DURATION,
        ease: "power2.out",
        onComplete: () => {
          // Allow natural height after animation
          el.style.height = "auto";

          // Scroll AFTER animation completes
          if (scrollParentRef?.current && itemEl) {
            scrollParentRef.current.scrollTo({
              top: itemEl.offsetTop - 100,
              behavior: "smooth",
            });
          }
        },
      });
    },
    {
      dependencies: [isActive],
    },
  );

  // Close animation with optional delay
  useEffect(() => {
    const el = contentRef.current;
    if (!el || isActive) return;

    const runClose = () => {
      if (prefersReducedMotion()) {
        el.style.height = "0";
        el.style.opacity = "0";
        setShouldRenderChildren(false);
        return;
      }

      gsap.to(el, {
        height: 0,
        opacity: 0,
        duration: 0.25,
        ease: "power2.inOut",
        onComplete: () => {
          setShouldRenderChildren(false);
        },
      });
    };

    if (delayClose) {
      closeTimeout.current = setTimeout(runClose, OPEN_DURATION * 1000);
    } else {
      runClose();
    }

    return () => {
      clearTimeout(closeTimeout.current);
    };
  }, [isActive, delayClose]);

  return (
    <div
      ref={itemRef}
      className={`acc-item ${isActive ? "acc-item-active" : ""}`}
    >
      <button
        ref={buttonRef}
        id={buttonId}
        aria-expanded={isActive}
        aria-controls={panelId}
        onClick={onToggle}
        type="button"
        className="acc-button"
      >
        <span>{title}</span>
        <Caret open={isActive} />
      </button>

      <div
        ref={contentRef}
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        style={{
          height: isActive ? "auto" : 0,
          opacity: isActive ? 1 : 0,
          overflow: "hidden",
        }}
      >
        <div>{shouldRenderChildren ? children : null}</div>
      </div>
    </div>
  );
};

export function ScreenerAccordion({
  defaultActiveKey,
  items,
  scrollParentRef,
}) {
  const [activeKey, setActiveKey] = useState(null);
  const [prevActiveKey, setPrevActiveKey] = useState(null);
  const buttonRefs = useRef([]);

  useEffect(() => {
    if (defaultActiveKey != null) {
      setActiveKey(defaultActiveKey);
    }
  }, [defaultActiveKey]);

  useEffect(() => {
    setPrevActiveKey(activeKey);
  }, [activeKey]);

  const focusButton = (index) => {
    buttonRefs.current[index]?.focus();
  };

  const onKeyDown = (e, index, itemKey) => {
    const lastIndex = items.length - 1;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        focusButton(index === lastIndex ? 0 : index + 1);
        break;

      case "ArrowUp":
        e.preventDefault();
        focusButton(index === 0 ? lastIndex : index - 1);
        break;

      case "Home":
        e.preventDefault();
        focusButton(0);
        break;

      case "End":
        e.preventDefault();
        focusButton(lastIndex);
        break;

      case "Enter":
      case " ":
        e.preventDefault();
        setActiveKey(activeKey === itemKey ? null : itemKey);
        break;

      default:
        break;
    }
  };

  return (
    <div className="acc-container">
      {items.map((item, index) => {
        const buttonId = `accordion-button-${item.key}`;
        const panelId = `accordion-panel-${item.key}`;
        const isActive = activeKey === item.key;

        const delayClose =
          !isActive && prevActiveKey !== null && prevActiveKey !== item.key;

        return (
          <div key={item.key} onKeyDown={(e) => onKeyDown(e, index, item.key)}>
            <AccordionItem
              title={item.name}
              isActive={isActive}
              delayClose={delayClose}
              onToggle={() => setActiveKey(isActive ? null : item.key)}
              buttonRef={(el) => (buttonRefs.current[index] = el)}
              buttonId={buttonId}
              panelId={panelId}
              scrollParentRef={scrollParentRef}
            >
              <ScreenerGrid
                projectKey={item.key}
                activeKey={activeKey}
                projectName={item.name}
              />
            </AccordionItem>
          </div>
        );
      })}
    </div>
  );
}
